import { useEffect, useState } from "react";
import { formatVndDigits } from "../../shared/i18n/index.ts";
import { interpolate, type Segment } from "../../shared/price.ts";
import { serverNow } from "../lib/clock.ts";

type Seg = Pick<Segment, "price" | "rate_per_s" | "server_ts" | "status" | "bucket">;

type Props = {
  segment: Seg;
  size?: "sm" | "md" | "lg";
  /** Struck-through start price, shown when it differs from the current price. */
  startPrice?: number;
  /** ms delay before the first tick; use tickStagger(id) in grids. */
  stagger?: number;
};

type State = { seg: Seg; value: number; chars: string[]; changed: boolean[]; n: number };

const stateByBucket = {
  days: "far",
  under_day: "far",
  hours: "near",
  ending_soon: "ending",
} as const;

function fresh(seg: Seg): State {
  const value = interpolate(seg, serverNow());
  return { seg, value, chars: formatVndDigits(value).split(""), changed: [], n: 0 };
}

function tick(s: State): State {
  const value = interpolate(s.seg, serverNow());
  if (value === s.value) return s;
  const chars = formatVndDigits(value).split("");
  const changed = chars.map((c, i) => s.chars[i] !== undefined && s.chars[i] !== c);
  return { seg: s.seg, value, chars, changed, n: s.n + 1 };
}

/**
 * Ticks once a second from a segment (client interpolation, 08-design.md
 * §Motion). Only digits that changed get the 120 ms opacity dip; the
 * currency sign and separators never move. aria-live is off on purpose.
 */
export function PriceDisplay({ segment, size = "md", startPrice, stagger = 0 }: Props) {
  const [state, setState] = useState<State>(() => fresh(segment));
  // Derive from props when the segment object changes (new poll result).
  if (state.seg !== segment) setState(fresh(segment));

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    const timeout = setTimeout(() => {
      setState(tick);
      interval = setInterval(() => setState(tick), 1000);
    }, stagger);
    return () => {
      clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [stagger]);

  const visual = segment.status === "active" ? stateByBucket[segment.bucket] : "ended";
  return (
    <span className={`price price--${size} price--${visual}`} aria-live="off">
      <span className="price__value">
        {state.chars.map((ch, i) => (
          <span
            key={state.changed[i] ? `${i}-${state.n}` : `${i}-${state.chars.length}`}
            className={`price__ch${state.changed[i] ? " tick" : ""}`}
          >
            {ch}
          </span>
        ))}
        <span className="price__unit">₫</span>
      </span>
      {startPrice !== undefined && startPrice > state.value ? (
        <s className="price__start">{formatVndDigits(startPrice)} ₫</s>
      ) : null}
    </span>
  );
}
